import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  TrendUp,
  TrendDown,
  Minus,
  Target,
  Globe
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SDGTrackerPage = () => {
  const { token } = useAuth();
  const [sdgData, setSDGData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSDGData();
    // Set up auto-refresh every 30 seconds for "live" feel
    const interval = setInterval(fetchSDGData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSDGData = async () => {
    try {
      const response = await axios.get(`${API}/sdg/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSDGData(response.data);
    } catch (error) {
      console.error('Failed to fetch SDG data:', error);
      toast.error('Failed to load SDG metrics');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendUp size={20} weight="bold" className="text-emerald-500" />;
      case 'down':
        return <TrendDown size={20} weight="bold" className="text-red-500" />;
      default:
        return <Minus size={20} weight="bold" className="text-gray-500" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-emerald-500';
      case 'down':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white/60 text-sm uppercase tracking-widest">Loading SDG Tracker...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="sdg-tracker-page">
      <Navbar />
      
      <div className="max-w-[1920px] mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="glass-panel rounded-sm p-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white mb-3 tracking-tight">
                Live SDG Tracker
              </h1>
              <p className="text-gray-400 text-lg mb-4">
                Real-time monitoring of Sustainable Development Goals impact
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span>Live data • Updates every 30 seconds</span>
              </div>
            </div>
            <div className="text-right">
              <Globe size={80} weight="duotone" className="text-emerald-500/30 mb-2" />
              <p className="text-xs text-gray-400 uppercase tracking-wider">Total Impact Score</p>
              <p className="text-5xl font-black text-emerald-500">
                {sdgData?.total_impact_score?.toFixed(1) || '0'}
              </p>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sdgData?.metrics.map((metric) => (
            <div
              key={metric.sdg_number}
              className="glass-panel rounded-sm p-6 hover:scale-[1.02] transition-transform duration-200"
              data-testid={`sdg-card-${metric.sdg_number}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Target size={24} weight="duotone" className="text-emerald-500" />
                  <span className="text-2xl font-black text-white">SDG {metric.sdg_number}</span>
                </div>
                {getTrendIcon(metric.trend)}
              </div>

              {/* Name */}
              <h3 className="text-lg font-bold text-white mb-4">{metric.name}</h3>

              {/* Value */}
              <div className="mb-4">
                <p className="text-4xl font-black text-emerald-500 mb-1">
                  {metric.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <p className="text-sm text-gray-400">{metric.unit}</p>
              </div>

              {/* Trend */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Trend</span>
                <span className={`text-xs font-bold uppercase tracking-wider ${getTrendColor(metric.trend)}`}>
                  {metric.trend}
                </span>
              </div>

              {/* Last Updated */}
              <div className="mt-2 text-xs text-gray-600">
                Updated: {new Date(metric.last_updated).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>

        {/* Info Panel */}
        <div className="glass-panel rounded-sm p-6">
          <h3 className="text-lg font-bold text-white mb-3">About SDG Tracking</h3>
          <p className="text-sm text-gray-400 mb-4">
            GeoDominus tracks 7 core Sustainable Development Goals using real-time geospatial intelligence, 
            satellite data fusion, and AI-powered analytics. Our metrics are continuously updated from global 
            monitoring stations, satellite imagery, and IoT sensor networks.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-white/5 rounded-sm">
              <p className="text-2xl font-black text-white mb-1">24/7</p>
              <p className="text-xs text-gray-400">Continuous Monitoring</p>
            </div>
            <div className="p-4 bg-white/5 rounded-sm">
              <p className="text-2xl font-black text-white mb-1">1,200+</p>
              <p className="text-xs text-gray-400">Active Sensors</p>
            </div>
            <div className="p-4 bg-white/5 rounded-sm">
              <p className="text-2xl font-black text-white mb-1">99.9%</p>
              <p className="text-xs text-gray-400">Data Accuracy</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
